import React, { useState, useRef, Fragment, useMemo, useCallback } from 'react';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import { ButtonV3 } from '../../ButtonV3';
import { useClickOutside } from '../../../hooks/useClickOutside';
import CheckboxField, { CheckboxFieldProps } from '../CheckboxField/CheckboxField';
import { ListBoxOption, Asterisk, FormLabel } from '../form.styles';
import Styled from './DropdownCheckboxList.styles';
import { DropdownTrigger } from '../common/DropdownTrigger/DropdownTrigger';
import { BoxDropdownTriggerProps } from '../common/DropdownTrigger/BoxDropdownTrigger';
import { CommonTriggerProps } from '../common/DropdownTrigger/types';
import { CustomDropdownTriggerProps } from '../common/DropdownTrigger/CustomDropdownTrigger';

export type OptionType = Omit<CheckboxFieldProps, 'id' | 'checked' | 'onChange'> & {
  id?: string;
  value: string | number;
  label: string | React.ReactNode;
};

export type DropdownCheckboxListProps = FieldArrayRenderProps<string | number, HTMLDivElement> & {
  /**
   * Optional classname applied to the outermost HTML element of the component.
   */
  className?: string;

  /**
   * The label _of the chip_ used to trigger the dropdown.  For a label _above_ the element
   * please use formLabel;
   */
  label: string | React.ReactElement;

  /**
   * Optional label appearing above the chip/dropdown
   */
  formLabel?: {
    label: string | React.ReactElement;
    isRequired?: boolean;
  };

  includeCountInLabel?: boolean;

  /**
   * The options rendered in the checkbox list.
   */
  options: OptionType[];

  /**
   * ID of the form control.  Will be used to generate sequential ids for each
   * checkbox in the list (if not provided in the option)
   */
  id: string;

  /**
   * Callback when the dropdown is toggled open or closed.  Invoked after the expanded
   * state changes.  Invoked on each toggle (open or closed).
   */
  onClick?: React.MouseEventHandler<HTMLElement>;

  /**
   * In the mobile takeover, a CTA rendered at the bottom of the screen.  Useful
   * for giving mobile users a tap target instead of using the X to close the
   * take over.  Does not act as an apply button (checks to form take affect
   * immediately).
   */
  mobileCtaProps?: {
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    label: string;
  };

  /**
   * In the mobile takeover, the header at the top of the picker.
   */
  mobileHeader?: string | React.ReactNode;

  /** displays caret to the right of text.  Default: false. */
  withCaret?: boolean;

  /** Disables the form element. Default: false */
  disabled?: boolean;

  /** Optional element to use to trigger the dropdown */
  customTrigger?: Omit<CustomDropdownTriggerProps, keyof CommonTriggerProps>;

  /**
   * Render the trigger in the same style as a dropdown
   *
   * @example
   * boxTrigger: {
   *    placeholder: "Please make a selection",
   * }
   *
   * @see BoxDropdownTrigger
   */
  boxTrigger?: Omit<BoxDropdownTriggerProps, keyof CommonTriggerProps>;
};

/**
 * Implements a multi-select dropdown of checkboxes.  The dropdown can be
 * triggered by a chip, a dropdown style box, or a custom element.  On mobile,
 * takes over the screen with an picker.  For use in react-final-form, use
 * a FieldArray from 'react-final-form-arrays' and arrayMutators from 'final-form-arrays';
 *
 * ## Example
 * Renders a checkbox list in the style of DropdownV3 with a form label above
 * the dropdown with a required field label.  In the mobile takeover, uses
 * a custom header and has an "Apply" CTA at the bottom.  The field, selected-events
 * will be an array of strings, with entries in the array indicating the
 * selected options
 *
 * ```typescript
 *
 * import DropdownCheckboxList from '@zola/zola-ui/src/components/Form/DropdownCheckboxList/DropdownCheckboxList';
 *
 * import { FieldArray } from 'react-final-form-arrays';
 * import arrayMutators from 'final-form-arrays';
 *
 * <Form
 *   mutators={{
 *     ...arrayMutators
 *   }}
 *   render={() => {
 *     return (
 *       <form>
 *         <FieldArray name="selected-events">
 *           {({ ...props }) => (
 *             <DropdownCheckboxList
 *               {...props}
 *               options={[ ... ]}
 *               boxTrigger={{ placeholder: "Please make a selection", isPlaceholder: selections.length === 0 }}
 *               formLabel={{ label: 'Which Events would you like photographed', isRequired: true }}
 *               mobileCtaProps={{
 *                 label: 'Apply',
 *               }}
 *               mobileHeader="Which events would you like photographed?"
 *             />
 *           )}
 *         </FieldArray>
 *       </form>
 *     )
 *   }}
 * />
 * ```
 *
 * ## Example
 * Renders a checkbox dropdown in a chip that animates the caret when the
 * dropdown is opened and closed.  The 4th option (white) is disabled.
 *
 * ```typescript
 * const colors:OptionType[] [
 *    { label: 'Red', value: '#F00' },
 *    { label: 'Green', value: '#0F0' },
 *    { label: 'Blue', value: '#00F' },
 *    { label: 'White', value: '#FFF', disabled: true },
 *    { label: 'Black', value: '#000' },
 *  ],
 *
 * <form>
 *   <FieldArray name="colors">
 *     {({ ...props }) => <DropdownCheckboxList options={colors}  withCaret {...props} />}
 *   </FieldArray>
 * </form>
 * ```
 *
 * @see DropdownCheckboxList.stories.tsx
 * @see form.stories
 */
const DropdownCheckboxList: React.FC<DropdownCheckboxListProps> = ({
  className,
  options,
  fields,
  id,
  label,
  formLabel,
  includeCountInLabel,
  onClick,
  mobileCtaProps,
  mobileHeader,
  withCaret,
  disabled,
  customTrigger,
  boxTrigger,
}) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setExpanded(false);
    },
    isOpen: expanded,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    if (e.target.checked) {
      fields.push(value);
    } else {
      fields.remove(fields.value.indexOf(value));
    }
  };

  const toggleDropdown: React.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      e.preventDefault();
      setExpanded(!expanded);
      if (onClick) {
        onClick(e);
      }
    },
    [expanded, onClick]
  );

  const numSelected = fields?.value?.length || 0;

  // The label that appears in a chip trigger
  const formatLabel = useMemo(() => {
    const countInLabel = includeCountInLabel && numSelected > 0 ? ` (${numSelected})` : '';
    return (
      <Fragment>
        {label}
        {countInLabel}
      </Fragment>
    );
  }, [includeCountInLabel, label, numSelected]);

  const handleMobileCtaClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setExpanded(false);
    if (mobileCtaProps?.onClick) {
      mobileCtaProps.onClick(e);
    }
  };

  // Determines what type of trigger will be used to open the dropdown.  Because the
  // box and custom trigger were retrofitted into this component without changing
  // the interface, the trigger props for DropdownCheckboxList are different than
  // DropdownV3.  This builds a consistent set of props to pass to the trigger
  const triggerProps = useMemo(() => {
    if (boxTrigger) {
      return { ...boxTrigger };
    }
    if (customTrigger) {
      return { ...customTrigger };
    }

    return {
      chips: true,
      selected: expanded || numSelected > 0,
      withCaret,
      label,
      formatLabel,
    };
  }, [boxTrigger, customTrigger, expanded, numSelected, formatLabel, label, withCaret]);

  return (
    <Styled.DropdownCheckboxListWrapper
      className={className}
      hasLabel={Boolean(formLabel)}
      ref={ref}
    >
      {formLabel && (
        <FormLabel htmlFor={id}>
          {formLabel.label}
          {formLabel.isRequired && <Asterisk>*</Asterisk>}
        </FormLabel>
      )}

      <DropdownTrigger
        expanded={expanded}
        toggleDropdown={toggleDropdown}
        handleKeyDown={() => null}
        id={id}
        disabled={disabled || (options || []).length < 1}
        {...triggerProps}
      />
      {expanded && (
        <Styled.StyledDropdownListBox
          tabIndex={0}
          onClose={() => setExpanded(false)}
          mobileHeader={mobileHeader || label}
          mobileCtaContent={
            mobileCtaProps && (
              <ButtonV3 fullWidth onClick={handleMobileCtaClick}>
                {mobileCtaProps?.label}
              </ButtonV3>
            )
          }
        >
          {options?.map((option, i) => {
            const { value, label: checkboxLabel, ...rest } = option;
            return (
              <ListBoxOption key={`${id}-${i}`}>
                <CheckboxField
                  id={`${id}-${i}`}
                  label={checkboxLabel}
                  checked={fields?.value?.includes(value)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, value)}
                  {...rest}
                />
              </ListBoxOption>
            );
          })}
        </Styled.StyledDropdownListBox>
      )}
    </Styled.DropdownCheckboxListWrapper>
  );
};

export default DropdownCheckboxList;
